/**
 * WebJsonField — form-style JSON renderer.
 *
 * Renders any JSON value as a professional labelled form:
 *   - Primitives → label + value row
 *   - Objects    → collapsible sub-panel (key name as header, never "Object (N)")
 *   - Arrays     → collapsible sub-panel with item count; primitive arrays inline
 *
 * Usage:
 *   <WebJsonField value={anyObject} />               // root, no panel wrapper
 *   <WebJsonField label="item" value={obj} />        // named sub-panel
 */

import React, { useState } from 'react';
import './WebJsonField.css';

// ── Key label formatter ───────────────────────────────────────────────────────
// "executionID" → "Execution ID"  |  "startedAt" → "Started At"

export function fmtKey(k: string): string {
  return k
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // "URLPath" → "URL Path"
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')        // "nodeId"  → "node Id"
    .replace(/[_-]+/g, ' ')                        // underscores/hyphens → spaces
    .replace(/^./, s => s.toUpperCase())           // capitalize first letter
    .trim();
}

// ── Primitive value chip ──────────────────────────────────────────────────────

export const JsonPrimitive: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) return <span className="wjf-null">—</span>;
  if (typeof value === 'boolean') return <span className="wjf-bool">{value ? 'true' : 'false'}</span>;
  if (typeof value === 'number')  return <span className="wjf-num">{value}</span>;
  if (typeof value === 'string')  return <span className="wjf-str">{value}</span>;
  return <span>{String(value)}</span>;
};

// ── Recursive form renderer ───────────────────────────────────────────────────

export interface WebJsonFieldProps {
  label?: string;
  value: unknown;
  depth?: number;
}

export const WebJsonField: React.FC<WebJsonFieldProps> = ({ label, value, depth = 0 }) => {
  const [open, setOpen] = useState(true);

  // ── Primitive ──
  if (value === null || value === undefined || typeof value !== 'object') {
    if (!label) return <JsonPrimitive value={value} />;
    return (
      <div className="wjf-prop">
        <span className="wjf-prop__label">{fmtKey(label)}</span>
        <span className="wjf-prop__value"><JsonPrimitive value={value} /></span>
      </div>
    );
  }

  // ── Array ──
  if (Array.isArray(value)) {
    if (value.length === 0) {
      if (!label) return null;
      return (
        <div className="wjf-prop">
          <span className="wjf-prop__label">{fmtKey(label)}</span>
          <span className="wjf-prop__value wjf-null">—</span>
        </div>
      );
    }
    const allPrimitive = value.every(v => v === null || typeof v !== 'object');
    if (allPrimitive) {
      const joined = value.join(', ');
      if (!label) return <span className="wjf-str">{joined}</span>;
      return (
        <div className="wjf-prop">
          <span className="wjf-prop__label">{fmtKey(label)}</span>
          <span className="wjf-prop__value wjf-str">{joined}</span>
        </div>
      );
    }
    return (
      <div className="wjf-panel">
        <button className="wjf-panel__hdr" onClick={() => setOpen(o => !o)}>
          <span className="wjf-panel__arrow">{open ? '▾' : '▸'}</span>
          {label && <span className="wjf-panel__name">{fmtKey(label)}</span>}
          <span className="wjf-panel__count">{value.length} items</span>
        </button>
        {open && (
          <div className="wjf-panel__body">
            {value.map((item, i) => (
              <WebJsonField key={i} label={`Item ${i + 1}`} value={item} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Object ──
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined);
  if (entries.length === 0) {
    if (!label) return null;
    return (
      <div className="wjf-prop">
        <span className="wjf-prop__label">{fmtKey(label)}</span>
        <span className="wjf-prop__value wjf-null">—</span>
      </div>
    );
  }

  // Root (no label) → flat form rows, no wrapping panel
  if (!label) {
    return (
      <div className="wjf-props">
        {entries.map(([k, v]) =>
          v !== null && typeof v === 'object'
            ? <WebJsonField key={k} label={k} value={v} depth={depth} />
            : (
              <div key={k} className="wjf-prop">
                <span className="wjf-prop__label">{fmtKey(k)}</span>
                <span className="wjf-prop__value"><JsonPrimitive value={v} /></span>
              </div>
            )
        )}
      </div>
    );
  }

  // Named object → collapsible sub-panel with key name as header
  return (
    <div className="wjf-panel">
      <button className="wjf-panel__hdr" onClick={() => setOpen(o => !o)}>
        <span className="wjf-panel__arrow">{open ? '▾' : '▸'}</span>
        <span className="wjf-panel__name">{fmtKey(label)}</span>
      </button>
      {open && (
        <div className="wjf-panel__body">
          {entries.map(([k, v]) =>
            v !== null && typeof v === 'object'
              ? <WebJsonField key={k} label={k} value={v} depth={depth + 1} />
              : (
                <div key={k} className="wjf-prop">
                  <span className="wjf-prop__label">{fmtKey(k)}</span>
                  <span className="wjf-prop__value"><JsonPrimitive value={v} /></span>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};
